import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash-es';
import { Button, ChipButton, DataTable, DropdownMenu, DropdownMenuItem, Page, PageContentHeader, PageEmptyState, PageErrorState, Row, Typography, useOverlayContext, Spacing } from 'seagull';
import { ChevronDown, ChevronUp, Download, EmailSent, Eye, Import } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import AgreementStudioPage from '~/components/AgreementStudioPage/AgreementStudioPage';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import { importRequestModalConfig } from '~/components/ImportRequestModal/ImportRequestModal.utils';
import PageLoadingState from '~/components/PageLoadingState';
import TableFilters from '~/components/TableFilters/TableFilters';
import { routes } from '~/constants';
import { useProposals } from '~/hooks/reactQuery/queries/useProposals';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useProposalActions } from '~/hooks/useProposalActions';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { RelationshipStatus } from '~/models/relationship';
import { isExpired } from '~/models/relationship-summary';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { useProposalRowActions } from './hooks/useProposalRowActions';
import { useProposalsCsvExport } from './hooks/useProposalsCsvExport';
import { useProposalsLiveRefresh } from './hooks/useProposalsLiveRefresh';
import { useProposalViewing } from './hooks/useProposalViewing';
import { clientPreviewModalConfig } from './modals/clientPreviewModalConfig';
import { bulkRemindersRequest } from './modals/proposalsBulkReminders';
import { DEFAULT_PROPOSALS_SORT, PROPOSAL_COLUMN_NAMES, proposalColumns, proposalFilterConfig, proposalMainColumn, proposalMobileCard, proposalSentByOptions, proposalStatusFilterOptions, proposalStatusSkin, toProposalMenuRow, toProposalRows, visibleProposals } from './ProposalsDashboard.table.utils';
import { getProposalsStatusCounters } from './ProposalsDashboard.utils';
import { FilterType } from './ProposalsDashboardOld';
import { ProposalsIllustration } from './ProposalsIllustration';
const IMPORT_PROPOSALS_MODAL_BI = {
    close: {
        name: 'proposals_confirm'
    },
    completeForm: {
        name: 'import_proposals_complete_form'
    }
};
export const GetProposalsDashboardTID = (parentTestId)=>getTestIds(parentTestId);
const PAGE_SIZE = 100;
function ProposalsTable(param) {
    let { data, isRefreshing, refresh } = param;
    const { ProposalsDashboardTID: TID } = useMemo(()=>GetProposalsDashboardTID(), []);
    const { t: texts } = useLocaleKeys('proposal.dashboard');
    const isVendor = useStore(selectors.user.isVendor);
    const isViewMode = useStore(selectors.isViewMode);
    const isMobileDevice = useIsMobileForHomePage();
    const overlayContext = useOverlayContext();
    const { logBI } = useStoreActions();
    const { doBulkAction } = useBulkActionModal();
    const { api } = useStoreActions();
    const { downloadAgreementPdf } = useDownloadAgreementPdf();
    const proposalActions = useProposalActions();
    const rowActionsOf = useProposalRowActions();
    const csvExport = useProposalsCsvExport();
    const viewing = useProposalViewing({
        proposalActions,
        refresh
    });
    const mobile = useMemo(()=>proposalMobileCard(texts), []);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11303);
    const [selectedIds, setSelectedIds] = useState(()=>new Set());
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const [page, setPage] = useState(1);
    const { urlState: { filter, search, sentBy }, changeUrlState } = useStateFromUrl({
        filter: {
            multi: true,
            defaultValue: []
        },
        search: {
            defaultValue: ''
        },
        sentBy: {
            multi: true,
            defaultValue: [],
            delimeter: '|'
        }
    });
    const defaultSort = useMemo(()=>({
            colIndex: PROPOSAL_COLUMN_NAMES.indexOf(DEFAULT_PROPOSALS_SORT.name),
            sort: DEFAULT_PROPOSALS_SORT.direction
        }), []);
    const { sort: urlSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds: PROPOSAL_COLUMN_NAMES
    });
    const tableSort = useMemo(()=>{
        var _PROPOSAL_COLUMN_NAMES_, _ref;
        return {
            name: (_PROPOSAL_COLUMN_NAMES_ = PROPOSAL_COLUMN_NAMES[(_ref = urlSort === null || urlSort === void 0 ? void 0 : urlSort.colIndex) !== null && _ref !== void 0 ? _ref : defaultSort.colIndex]) !== null && _PROPOSAL_COLUMN_NAMES_ !== void 0 ? _PROPOSAL_COLUMN_NAMES_ : DEFAULT_PROPOSALS_SORT.name,
            direction: (urlSort === null || urlSort === void 0 ? void 0 : urlSort.sort) === 'asc' ? 'asc' : 'desc'
        };
    }, [
        urlSort === null || urlSort === void 0 ? void 0 : urlSort.colIndex,
        urlSort === null || urlSort === void 0 ? void 0 : urlSort.sort,
        defaultSort.colIndex
    ]);
    const proposals = useMemo(()=>visibleProposals({
            proposals: data,
            isVendor,
            texts,
            statusFilter: filter,
            sentByFilter: sentBy,
            search,
            sort: tableSort
        }), [
        data,
        isVendor,
        tableSort,
        filter,
        search,
        sentBy
    ]);
    const proposalById = useMemo(()=>new Map(proposals.map((proposal)=>[
                proposal.id,
                proposal
            ])), [
        proposals
    ]);
    const badgeLabels = useMemo(()=>proposalStatusSkin({
            proposals,
            isVendor,
            texts
        }), [
        proposals,
        isVendor
    ]);
    const rows = useMemo(()=>toProposalRows({
            proposals,
            isVendor,
            texts
        }), [
        proposals,
        isVendor
    ]);
    const pagedRows = useMemo(()=>rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [
        rows,
        page
    ]);
    const columns = useMemo(()=>proposalColumns({
            texts,
            skinByLabel: badgeLabels.skinByLabel,
            infoByLabel: badgeLabels.infoByLabel
        }), [
        badgeLabels
    ]);
    const mainColumn = useMemo(()=>proposalMainColumn(texts), []);
    const counters = useMemo(()=>getProposalsStatusCounters(data, isVendor, texts), [
        data,
        isVendor
    ]);
    const statusOptions = useMemo(()=>proposalStatusFilterOptions({
            counters,
            isVendor,
            texts
        }), [
        isVendor,
        counters
    ]);
    const sentByOptions = useMemo(()=>proposalSentByOptions(data), [
        data
    ]);
    const filterConfig = useMemo(()=>proposalFilterConfig({
            statusOptions,
            sentByOptions
        }), [
        statusOptions,
        sentByOptions
    ]);
    const sortOptions = useMemo(()=>[
            {
                id: 'companyName',
                label: texts.columnHeaders.companyName(),
                defaultDirection: 'desc',
                colIndex: 0
            },
            {
                id: 'sentDate',
                label: texts.columnHeaders.sentDate(),
                defaultDirection: 'desc',
                colIndex: 1
            },
            {
                id: 'sentBy',
                label: texts.columnHeaders.sentBy(),
                defaultDirection: 'desc',
                colIndex: 2
            },
            {
                id: 'status',
                label: texts.columnHeaders.status(),
                defaultDirection: 'asc',
                colIndex: 3
            }
        ], []);
    const selectedProposalIds = useCallback(()=>[
            ...selectedIds
        ].map(String), [
        selectedIds
    ]);
    const openClientPreview = useCallback((relationshipId)=>overlayContext.showModal(clientPreviewModalConfig({
            relationshipId
        })), []);
    const columnMenu = useTableColumnMenu('proposals');
    const openImportModal = ()=>overlayContext.showModal(importRequestModalConfig('proposals_dashboard_import_modal_confirm', {
            textsPath: 'proposal.dashboard.importProposalsModal',
            bi: IMPORT_PROPOSALS_MODAL_BI,
            testId: TID.ImportModal()
        }));
    const onFiltersRefresh = (newFilters, newSort)=>{
        var _newFilters_FilterType_Status, _newFilters_FilterType_SentBy;
        changeUrlState({
            filter: (_newFilters_FilterType_Status = newFilters[FilterType.Status]) !== null && _newFilters_FilterType_Status !== void 0 ? _newFilters_FilterType_Status : [],
            sentBy: (_newFilters_FilterType_SentBy = newFilters[FilterType.SentBy]) !== null && _newFilters_FilterType_SentBy !== void 0 ? _newFilters_FilterType_SentBy : []
        });
        if (newSort) changeSort(newSort);
        setPage(1);
    };
    const onSortChange = (nextSort)=>{
        logBI(11300, {
            column: nextSort.name
        });
        changeSort({
            colIndex: PROPOSAL_COLUMN_NAMES.indexOf(nextSort.name),
            sort: nextSort.direction
        });
        setPage(1);
    };
    const resetFilters = ()=>{
        changeUrlState({
            filter: [],
            search: '',
            sentBy: []
        });
        setPage(1);
    };
    const remindersRequestFor = (proposalIds)=>bulkRemindersRequest({
            proposalIds,
            texts,
            sendReminder: api.relationship.sendProposalApprovalReminder,
            onConfirmed: ()=>logBI(11255, {
                    count: proposalIds.length
                }),
            onFailedIds: (failedIds)=>setSelectedIds(new Set(failedIds))
        });
    const sendBulkReminders = ()=>doBulkAction(remindersRequestFor(selectedProposalIds()));
    const sentProposalCount = data.filter((proposal)=>proposal.status !== RelationshipStatus.Expired).length;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(Page, {
            testId: TID.Root(),
            variant: "containedHeight",
            onRefresh: refresh,
            header: isVendor ? texts.vendorTitleWithCount({
                count: sentProposalCount
            }) : texts.clientTitleWithCount({
                count: sentProposalCount
            }),
            loading: isRefreshing,
            actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                        target: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'open_proposals_more_actions'
                            },
                            skin: "primaryOutline",
                            suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                            testId: TID.MoreActionsButton(),
                            children: texts.moreActions()
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                bi: {
                                    name: 'import_proposals'
                                },
                                iconStart: /*#__PURE__*/ _jsx(Download, {}),
                                testId: TID.ImportButton(),
                                onClick: openImportModal,
                                children: texts.importProposals()
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                bi: {
                                    name: 'export_all_proposals'
                                },
                                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                testId: TID.ExportAllButton(),
                                disabled: csvExport.exportingAll,
                                intent: Intent.ExportProposalsToCsv,
                                onClick: csvExport.exportAll,
                                children: texts.exportProposals()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CreateProposalButton, {})
                ]
            }),
            children: [
                viewing.viewProposalMode != null && /*#__PURE__*/ _jsx(AgreementStudioPage, {
                    disableDataFetching: true,
                    viewProposalMode: viewing.viewProposalMode,
                    returnRoute: routes.proposalsDashboard,
                    onClose: viewing.closeViewProposal
                }),
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsxs(Row, {
                        gap: Spacing.Space8,
                        alignItems: "center",
                        justifyContent: "space-between",
                        wrap: true,
                        children: [
                            /*#__PURE__*/ _jsx(TableFilters, {
                                testIdPrefix: "proposals-dashboard",
                                search: {
                                    bi: {
                                        name: 'proposals_search'
                                    },
                                    value: search,
                                    testId: TID.Search(),
                                    registerHotkey: true,
                                    onChange: (event)=>{
                                        changeUrlState({
                                            search: event.target.value
                                        });
                                        debouncedLogSearchToBI.onChange(event.target.value);
                                        setPage(1);
                                    },
                                    onClear: ()=>{
                                        changeUrlState({
                                            search: ''
                                        });
                                        debouncedLogSearchToBI.onClear();
                                        setPage(1);
                                    }
                                },
                                filterLabels: {
                                    [FilterType.Status]: texts.proposalStatusFilter(),
                                    [FilterType.SentBy]: texts.sentByFilter()
                                },
                                filterConfig: filterConfig,
                                selectedFilters: {
                                    [FilterType.Status]: filter,
                                    [FilterType.SentBy]: sentBy
                                },
                                setSelectedFilters: noop,
                                onRefresh: onFiltersRefresh,
                                onClear: !filter.length && !search && !sentBy.length ? undefined : resetFilters,
                                mobile: {
                                    sortOptions,
                                    sort: urlSort
                                }
                            }),
                            isVendor && !isMobileDevice && /*#__PURE__*/ _jsxs(Row, {
                                gap: Spacing.Space8,
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        testId: TID.BulkCounter(),
                                        children: selectedIds.size > 0 ? texts.selectedCount({
                                            count: selectedIds.size
                                        }) : ''
                                    }),
                                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                                        onOpenChange: setBulkActionsOpen,
                                        target: /*#__PURE__*/ _jsx(ChipButton, {
                                            bi: {
                                                name: 'proposals_bulk_actions'
                                            },
                                            testId: TID.BulkActions(),
                                            suffixIcon: bulkActionsOpen ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                                            highlight: bulkActionsOpen,
                                            disabled: selectedIds.size === 0,
                                            children: texts.bulkActions()
                                        }),
                                        children: [
                                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                bi: {
                                                    name: 'proposals_dashboard_bulk_send_reminders'
                                                },
                                                iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                                                testId: TID.BulkSendReminders(),
                                                onClick: sendBulkReminders,
                                                children: texts.sendReminders()
                                            }),
                                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                bi: {
                                                    name: 'proposals_dashboard_bulk_export_csv'
                                                },
                                                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                                testId: TID.BulkExportCsv(),
                                                disabled: csvExport.exportingSelected,
                                                intent: Intent.ExportProposalsToCsv,
                                                onClick: ()=>csvExport.exportSelected(selectedProposalIds()),
                                                children: texts.bulkExport()
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(DataTable, {
                    allowColumnResize: true,
                    allowColumnReorder: true,
                    reorderBi: {
                        name: 'proposals_reorder_columns'
                    },
                    allowColumnHide: true,
                    columnMenu: columnMenu,
                    mainColumn: mainColumn,
                    columns: columns,
                    items: pagedRows,
                    getRowId: (row)=>row.id,
                    mobile: mobile,
                    state: rows.length === 0 ? 'empty' : 'default',
                    rowHeapId: (row)=>{
                        var _proposalById_get;
                        return ((_proposalById_get = proposalById.get(row.id)) === null || _proposalById_get === void 0 ? void 0 : _proposalById_get.isSample) === true ? 'test-proposal' : 'proposals';
                    },
                    rowIntent: ()=>isVendor && !isViewMode ? undefined : Intent.ClientViewProposal,
                    onRowClick: (row)=>openClientPreview(row.id),
                    sort: {
                        name: tableSort.name,
                        direction: tableSort.direction,
                        sortedBy: 'consumer',
                        onChange: onSortChange
                    },
                    selection: isVendor && !isMobileDevice ? {
                        selectedIds,
                        onChange: setSelectedIds
                    } : undefined,
                    rowActions: {
                        menuBi: {
                            name: 'proposals_actions'
                        },
                        menuHeapId: 'proposals-menu-btn',
                        menuTitle: (row)=>row.companyName,
                        menuSubtitle: (row)=>row.proposalTitle,
                        inline: (row)=>{
                            const proposal = proposalById.get(row.id);
                            return proposal == null ? [] : [
                                {
                                    icon: Eye,
                                    label: isViewMode ? texts.mainAction.view() : texts.mainAction.preview(),
                                    intent: isViewMode ? Intent.ClientViewProposal : undefined,
                                    onClick: ()=>openClientPreview(proposal.id),
                                    bi: {
                                        name: 'proposals_primary_action'
                                    }
                                }
                            ];
                        },
                        menu: (row)=>{
                            const proposal = proposalById.get(row.id);
                            if (proposal == null) return [];
                            return rowActionsOf({
                                proposal: toProposalMenuRow(proposal),
                                isVendor,
                                expired: isExpired(proposal),
                                proposalActions,
                                approveOnBehalf: {
                                    withdrawnForEditing: proposal.withdrawnForEditing,
                                    hasExplicitApprovalToS: proposal.hasExplicitApprovalToS,
                                    hasSignatureToS: proposal.hasSignatureToS,
                                    hasServiceBundles: proposal.hasServiceBundles,
                                    hasOptionalServices: proposal.hasOptionalServices,
                                    hasBilledImmediatelyService: proposal.hasBilledImmediatelyService
                                },
                                onViewProposal: viewing.viewProposal,
                                onViewActivityLog: viewing.viewActivityLog,
                                onDownloadPDF: (menuProposal)=>downloadAgreementPdf(menuProposal.id),
                                refresh
                            });
                        }
                    },
                    pagination: {
                        page,
                        pageSize: PAGE_SIZE,
                        total: rows.length,
                        onPageChange: setPage
                    },
                    emptyState: /*#__PURE__*/ _jsx(PageEmptyState, {
                        testId: TID.NoMatches(),
                        illustration: /*#__PURE__*/ _jsx(ProposalsIllustration, {}),
                        title: texts.noMatchingFilters.title(),
                        message: texts.noMatchingFilters.message(),
                        actions: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'reset_proposals_filters'
                            },
                            testId: TID.ResetFilters(),
                            onClick: resetFilters,
                            children: texts.removeAllFilters()
                        })
                    }),
                    testId: TID.Table()
                })
            ]
        })
    });
}
export function ProposalsDashboard() {
    const { proposals, isLoading, isFetching, refresh, error } = useProposals({
        refreshOnHookMount: true
    });
    useProposalsLiveRefresh(refresh);
    const { ProposalsDashboardTID: TID } = useMemo(()=>GetProposalsDashboardTID(), []);
    const isVendor = useStore(selectors.user.isVendor);
    const { t: texts } = useLocaleKeys('proposal.dashboard');
    const title = isVendor ? texts.vendorTitle() : texts.clientTitle();
    useSetDocumentTitle(title);
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            title: title,
            onRefresh: refresh,
            pullToRefresh: true
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: title,
            loading: true
        });
    }
    if (proposals.length === 0) {
        return /*#__PURE__*/ _jsx(Page, {
            header: title,
            loading: isFetching,
            variant: "containedHeight",
            onRefresh: refresh,
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: TID.Empty(),
                illustration: /*#__PURE__*/ _jsx(ProposalsIllustration, {}),
                title: isVendor ? texts.emptyState.vendorTitle() : undefined,
                message: isVendor ? texts.emptyState.vendorMessage() : texts.emptyState.clientMessage(),
                actions: isVendor ? /*#__PURE__*/ _jsx(CreateProposalButton, {}) : undefined
            })
        });
    }
    return /*#__PURE__*/ _jsx(ProposalsTable, {
        data: proposals,
        isRefreshing: isFetching,
        refresh: refresh
    });
}
